import React from "react";
import Footer from "./Footer";
import Navbar from "./Navbar";
import NavbarWithPushContent from "./NavbarWithPushContent";
import { FaCertificate, FaCheckCircle, FaAward, FaCannabis } from 'react-icons/fa';
import useAboutUs from "../Hooks/useAboutUs";
import LoadingSpinner from "../Assets/LoadingSpinner";
import ErrorComponent from "../Assets/ErrorComponent";
import { useTranslation } from "react-i18next";
import Reveal from "../Assets/Reveal";
import Seo from "../Assets/Seo";

type ProductCategory = {
  _id: string;
  name: string;
  subcategories: { name: string; products: any[] }[];
  products: any[];
};

interface AboutUsProps {
  products: ProductCategory[];
  lang: string;
}

const AboutUs: React.FC<AboutUsProps> = ({ products, lang }) => {
  const { t } = useTranslation();
  const { aboutUsData, loading, error } = useAboutUs();

  const certifications = [
    { icon: <FaCertificate className="w-6 h-6" />, title: t("certifications.euGmp") },
    { icon: <FaCheckCircle className="w-6 h-6" />, title: t("certifications.localGmp") },
    { icon: <FaAward className="w-6 h-6" />, title: t("certifications.iso") },
    { icon: <FaCannabis className="w-6 h-6" />, title: t("certifications.cbdLicense") },
  ];

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorComponent message={error} />

  return (
    <div className="flex flex-col min-h-screen bg-white">
      <Seo title={t("seo.about.title")} description={t("seo.about.description")} lang={lang} />
      <div className="hidden md:block">
        <NavbarWithPushContent products={products} lang={lang} />
      </div>
      <div className="block md:hidden">
        <Navbar products={products} lang={lang} />
      </div>

      {/* Page header */}
      <header className="bg-gray-100 px-4 pt-28 md:pt-36 pb-12">
        <div className="max-w-6xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-brand-gray">
            {t("navbar.aboutUs")}
          </h1>
          <span className="mx-auto mt-5 block h-1 w-20 rounded-full bg-brand-400" />
        </div>
      </header>

      {/* Certifications */}
      <section className="bg-white px-4 py-16">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {certifications.map((item, index) => (
              <Reveal key={index} delay={index * 100} className="h-full">
                <div className="group flex h-full flex-col items-center gap-4 rounded-2xl border border-brand-100 bg-brand-50 p-6 text-center transition-all duration-300 hover:-translate-y-1 hover:shadow-lg">
                  <div className="flex h-14 w-14 items-center justify-center rounded-full bg-brand-400 text-white transition-colors duration-300 group-hover:bg-brand-500">
                    {item.icon}
                  </div>
                  <div className="text-sm font-bold uppercase leading-snug tracking-wide text-brand-gray">
                    {item.title}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Dynamic About Us / story sections */}
      <section className="bg-gray-50 px-4 py-16">
        <div className="max-w-6xl mx-auto space-y-20 md:space-y-28">
          {aboutUsData.map((entry, index) => {
            const hasImages = (entry.images?.length ?? 0) > 0;
            return (
              <Reveal key={index}>
                <article>
                  {(entry.name?.[lang] || entry.subcategory?.[lang]) && (
                    <div className="mb-8 text-center sm:text-left">
                      {entry.name?.[lang] && (
                        <>
                          <h2 className="text-3xl sm:text-4xl font-bold text-brand-gray">
                            {entry.name?.[lang]}
                          </h2>
                          <span className="mx-auto mt-4 block h-1 w-16 rounded-full bg-brand-400 sm:mx-0" />
                        </>
                      )}
                      {entry.subcategory?.[lang] && (
                        <p className="mt-3 text-lg sm:text-xl font-semibold text-brand-500">
                          {entry.subcategory?.[lang]}
                        </p>
                      )}
                    </div>
                  )}

                  <div
                    className={`flex flex-col items-center gap-10 ${
                      hasImages
                        ? index % 2 === 1
                          ? "lg:flex-row-reverse"
                          : "lg:flex-row"
                        : ""
                    }`}
                  >
                    <div className={hasImages ? "w-full lg:w-1/2" : "w-full"}>
                      <div
                        className="prose max-w-none text-gray-600 leading-relaxed ql-editor text-left"
                        style={{ padding: 0 }}
                        dangerouslySetInnerHTML={{ __html: entry.text?.[lang] }}
                      />
                    </div>

                    {hasImages && (
                      <div className="flex w-full flex-col gap-6 lg:w-1/2">
                        {entry.images.map((imgUrl, i) => (
                          <img
                            key={i}
                            src={imgUrl}
                            alt={entry.name?.[lang] || "Galafarm"}
                            className="w-full h-auto rounded-2xl shadow-md"
                          />
                        ))}
                      </div>
                    )}
                  </div>
                </article>
              </Reveal>
            );
          })}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutUs;
